import {
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Menu as MenuIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  Dashboard as DashboardIcon,
  CorporateFare as CorporateFareIcon,
  Business as BusinessIcon,
  AddLocationAlt as AddLocationAltIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  Segment as SegmentIcon,
  AppRegistration as AppRegistrationIcon,
  Apartment as ApartmentIcon,
  LocalPolice as LocalPoliceIcon,
  AccountCircleOutlined,
  PeopleAltOutlined,
} from "@mui/icons-material";
import { LogIn } from "lucide-react";
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  createTheme,
  ThemeProvider,
  MenuItem,
  Tooltip,
  Avatar,
  Popover,
  useMediaQuery,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { MAIN_URL , OTHER_LIVE_URL } from "../../Configurations/Urls";
import useEmployeeDataStore from "../../Zustand/Store/useEmployeeDataStore";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import ExpandedSideOption from "../../Routes/Layouts/ExpandedSideOption";
import {
  canAccessOrganizationList,
  hasPermissionAction,
} from "../../Utils/organizationPermissions";

export default function SideBar({ Permission }) {
  console.log("permission coming is : ", Permission);

  const { logout, userData, setAccess, login, reporting_manager_employees, reporting_manager_interns } = useAuthStore();
  const [darkMode, setDarkMode] = useState(() => {
    try {
      return localStorage.getItem("pm_dark_mode") === "true";
    } catch {
      return false;
    }
  });
  const [collapsed, setCollapsed] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(310);
  const [isResizing, setIsResizing] = useState(false);
  const { resetData } = useEmployeeDataStore();

  const isMobile = useMediaQuery("(max-width:768px)");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("pm_dark_mode", String(darkMode));
    } catch {
      // Ignore storage failures (private mode / blocked storage).
    }
  }, [darkMode]);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: darkMode ? "dark" : "light",
        },
      }),
    [darkMode]
  );

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to logout?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("token");

          await axios.get(`${OTHER_LIVE_URL}/api/auth/logout`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          logout();
          localStorage.removeItem("token");

          Swal.fire("Logged out!", "You have been logged out.", "success");
          navigate("/login");
        } catch (error) {
          console.error("Logout failed:", error);
          Swal.fire("Error", "Logout failed. Please try again.", "error");
        }
      }
    });
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Determine permission based on reporting manager arrays
  const taskPermission = useMemo(() => {
    const hasReportingEmployees = reporting_manager_employees?.length > 0;
    const hasReportingInterns = reporting_manager_interns?.length > 0;
    const shouldUseTeamTasks = hasReportingEmployees || hasReportingInterns;
    return shouldUseTeamTasks ? "VIEW_TEAM_TASKS" : "VIEW_ALL_TASKS";
  }, [reporting_manager_employees, reporting_manager_interns]);

  const sidebarOptions = useMemo(() => [
    { kind: "header", title: "" },
    {
      heading: "Dashboard",
      route: "/",
      title: "Dashboard",
      icon: <DashboardIcon />,
      PermissionKey: "PM_DASHBOARD",
    },
    { kind: "divider" },
    {
      heading: "Organization SetUp",
      title: "Organization SetUp",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/organization/details",
          title: "Organizations",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "ORGANIZATION_ALL",
        },
        {
          route: "/organization/profile",
          title: "Organization Profile",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "ORGANIZATION_PROFILE_ALL",
        },
        {
          route: "/organization/entity",
          title: "Organization Entities",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "ENTITY_ALL",
        },
        {
          route: "/organization/location",
          title: "Locations",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "LOCATION_ALL",
        },
        {
          route: "/organization/application-access",
          title: "Application Access",
          icon: <SegmentIcon fontSize="small" />,
          permissionAction: "VIEW_ORGANIZATION_APPLICATION||ORGANIZATION_ALL",
        },
        {
          route: "/organization/departments",
          title: "Departments",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "DEPARTMENT_ALL",
        },
        {
          route: "/organization/designation",
          title: "Designations",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "DESIGNATION_ALL",
        },
        {
          route: "/organization/work-shift",
          title: "Workshifts",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "SHIFT_VIEW_ALL",
        },
        {
          route: "/organization/data-grid-settings",
          title: "Data Grid Settings",
          icon: <SegmentIcon fontSize="small" />,
          permissionAction: "ORGANIZATION_ALL||GRID_CONFIGRATION",
        },
      ],
    },

    {
      heading: "Employee Management",
      title: "Employee Management",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/organization/employee/employee-details",
          title: "Employees",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "EMPLOYEE_VIEW_ALL",
        },
        {
          route: "/organization/employee/employee-exits",
          title: "Employee Exits",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "EMPLOYEE_EXIT_VIEW_ALL",
        },
      ],
    },

    {
      heading: "User management",
      title: "User management",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/users",
          title: "Users",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "USER_VIEW_ALL",
        },
        {
          route: "/application/user-roles",
          title: "User Roles",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "USER_ROLE_ALL",
        },
        {
          route: "/application/user-role-permission",
          title: "User Role Permissions",
          icon: <SegmentIcon fontSize="small" />,
          permissionAction:
            "USER_ROLE_PERMISSION_ALL||USER_ROLE_PERMISSION_ASSIGN||USER_ROLE_PERMISSION_REMOVE||USER_ROLE_PERMISSION_ADD||USER_ROLE_PERMISSION_EDIT||USER_ROLE_PERMISSION_DELETE",
        },
        {
          route: "/application/user-permission",
          title: "User Permission Overrides",
          icon: <SegmentIcon fontSize="small" />,
          permissionAction:
            "USER_PERMISSION_ALL||USER_PERMISSION_EDIT||USER_PERMISSION_DELETE",
        },
      ],
    },

    {
      heading: "Tasks",
      title: "Tasks",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/project-task",
          title: "Employee Tasks",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: taskPermission,
        },
        {
          route: "/project-task/interns",
          title: "Interns Tasks",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: taskPermission,
        },

        {
          route: "/project-task-categories",
          title: "Tasks Categories",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TASK_CATEGORY",
        },
        {
          route: "/project-task-subcategories",
          title: "Tasks Sub Categories",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TASK_SUBCATEGORIES",
        },

        {
          route: "/project-task-schedule",
          title: "Employee Task Schedule",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_EMPLOYEE_TASK_SCHEDULE",
        },
        {
          route: "/project-task-schedule/interns",
          title: "Intern Task Schedule",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_INTERN_TASK_SCHEDULE",
        },
        {
          route: "/project-task-schedule-timelog",
          title: "Employee Task Schedule TimeLogs",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_EMPLOYEE_TASK_SCHEDULE_TIMELOGS",
        },
        {
          route: "/project-task-schedule-timelog/interns",
          title: "Intern Task Schedule TimeLogs",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_INTERN_TASK_SCHEDULE_TIMELOGS",
        },
      
        {
          route: "/project-task-recurrence",
          title: "Task Recurrences",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TASK_RECURRENCES",
        },
        {
          route: "/project-task-template",
          title: "Task Templates",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TASK_TEMPLATES",
        },
        {
          route: "/project-task-type",
          title: "Task Types",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TASK_TYPES",
        },
        {
          route: "/project-task/MassCopy",
          title: "Mass copy",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: taskPermission,
        },
        
      ],
    },

    {
      heading: "Organization Projects",
      title: "Organization Projects",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/project",
          title: "Projects",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_ORGANIZATON_PROJECTS",
        },
        {
          route: "/project-team",
          title: "Project Teams",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_TEAMS",
        },
        {
          route: "/project-team-member",
          title: "Project Team Members",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_TEAM_MEMBERS",
        },
        {
          route: "/project-milestone",
          title: "Project MileStones",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_MILESTONES",
        },
        {
          route: "/project-reports",
          title: "Project Reports",
          icon: <SegmentIcon fontSize="small" />,
          // PermissionKey: "VIEW_PROJECT_MILESTONES",
        }
      ],
    },

    {
      heading: "Organization Clients",
      title: "Organization Clients",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/client",
          title: "Clients",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_ORGANIZATION_CLIENTS",
        },
        {
          route: "/client-contact",
          title: "Clients Contacts",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_CLIENT_CONTACTS",
        },

      ],
    },
    {
      heading: "Project Templates",
      title: "Project Templates",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/project-templates",
          title: "Project Templates",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_TEMPLATES",
        },
        {
          route: "/project-templates-milestone",
          title: "Template MileStones",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TEMPLATE_MILESTONES",
        },
        {
          route: "/project-template-tasks",
          title: "Template Tasks",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_TEMPLATE_TASK",
        },
      ],
    },
    {
      heading: "Project Setup",
      title: "Project Setup",
      icon: <SegmentIcon />,
      children: [
        {
          route: "/project-type",
          title: "Project Types",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_TYPE",
        },
        {
          route: "/project-category",
          title: "Project Categories",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_CATEGORY",
        },
        {
          route: "/project-sub-category",
          title: "Project Sub Categories",
          icon: <SegmentIcon fontSize="small" />,
          PermissionKey: "VIEW_PROJECT_SUBCATAGORY",
        },
      ],
    },

  ], [taskPermission]);

  console.log("sidebarOptions", sidebarOptions);

  const [sidebaroption, setSideBarOption] = useState(sidebarOptions);

console.log("sidebaroption",sidebaroption)
useEffect(() => {
  if (Permission?.length > 0) {
    const filtered = sidebarOptions
      .map((item) => {
        // If item has children -> filter children
        if (item.children) {
          const allowedChildren = item.children.filter((child) => {
            if (child?.route === "/organization/details") {
              return canAccessOrganizationList(Permission);
            }
            if (child?.route === "/organization/data-grid-settings") {
              return hasPermissionAction(
                Permission,
                "ORGANIZATION_ALL||GRID_CONFIGRATION"
              );
            }
            if (child.permissionAction) {
              return hasPermissionAction(Permission, child.permissionAction);
            }
            return child.PermissionKey
              ? hasPermissionAction(Permission, child.PermissionKey)
              : true;
          });

          // If no child is allowed → remove whole section
          if (allowedChildren.length === 0) return null;

          return { ...item, children: allowedChildren };
        }

        // If item has a PermissionKey → check
        if (item.PermissionKey) {
          return hasPermissionAction(Permission, item.PermissionKey) ? item : null;
        }

        // Items without permissionKey (like Dashboard or dividers) always remain
        return item;
      })
      .filter(Boolean); // remove null items

    setSideBarOption(filtered);
  }
}, [Permission, sidebarOptions]);


  const handleNavigation = (route) => {
    navigate(route);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      <List>
        {sidebaroption?.map((item, index) => {
          if (item.kind === "divider")
            return <Divider key={index} sx={{ my: 1 }} />;
          if (item.kind === "header")
            return (
              <Typography
                key={index}
                variant="subtitle2"
                sx={{
                  pl: 2,
                  pt: 1.5,
                  pb: 0.5,
                  color: "text.secondary",
                }}
              >
                {item.title}
              </Typography>
            );
          if (item.children) {
            return (
              <ExpandedSideOption
                collapsed={collapsed}
                item={item}
                key={index}
                onNavigate={handleNavigation}
              />
            );
          }
          return (
            <ListItem
              button
              key={item.heading}
              onClick={() =>
                item.title === "Logout"
                  ? handleLogout()
                  : handleNavigation(item.route)
              }
              sx={{
                py: 1,
                minWidth: "40px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
                ...(location.pathname === item.route && {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(144, 202, 249, 0.16)"
                      : "rgba(25, 118, 210, 0.12)",
                  "& .MuiListItemIcon-root": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiListItemText-primary": {
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                  },
                }),
              }}
            >
              {item.icon && (
                <ListItemIcon sx={{ minWidth: "35px", cursor: "pointer" }}>
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText primary={item.title} sx={{ cursor: "pointer" }} />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}


